import { Periods } from 'components/ProFormaReturnsTable/constant';
import { ICashFlowPeriod, IProFormaReturnsData } from 'views/ProFormaTable/types';

export const defaultPeriods = {
    'Jan 2023': 4364.63214488499,
    'Mar 2023': 2128.4748489905073,
    'Apr 2023': 1878.1144712397906,
    'May 2023': 3925.12447987615,
    'Jun 2023': 2294.2972849011485,
    'Jul 2023': 2295.6832216898906,
    'Aug 2023': 1893.506957332425,
    'Sep 2023': 2013.0654425893654,
    'Oct 2023': 2181.3692314871087,
    'Nov 2023': 1932.610521673614,
};

export const defaultReturnsData = {
    proFormaReturns: {
        totalDevelopmentCost: {
            amount: 0,
            periods: {},
        },
        totalInterest: {
            amount: 0,
            periods: {},
        },
        totalLoanDrawsDuringDevelopment: {
            amount: 0,
            periods: {},
        },
        permLoanFunding: {
            amount: 0,
            periods: {},
        },
        cashFlowFromOperations: {
            amount: 0,
            periods: {},
        },
        permLoanDebtService: {
            amount: 0,
            periods: {},
        },
        netSaleProceeds: {
            amount: 0,
            periods: {},
        },
        constructionLoanPayoff: {
            amount: 0,
            periods: {},
        },
        permanentLoanPayoff: {
            amount: 0,
            periods: {},
        },
        equityProceedsFromCapitalEvents: {
            amount: 0,
            periods: {},
        },
    },
    cashFlowData: {
        netUnleveredCashFlow: {
            amount: 0,
            equityMultiple: 0,
            irr: 0,
            netProfit: 0,
            periods: {},
        },
        netLeveredCashFlow: {
            amount: 0,
            equityMultiple: 0,
            irr: 0,
            netProfit: 0,
            periods: {},
        },
    },
    costMetrics: {
        yieldOnCost: {
            untrended: 0,
            trended: 0,
            sale: 0,
            isHighlighted: null,
        },
        marketCapRate: {
            untrended: 0,
            trended: 0,
            sale: 0,
            isHighlighted: null,
        },
        developmentSpread: {
            untrended: 0,
            trended: 0,
            sale: 0,
            isHighlighted: null,
        },
    },
};

export const transformReturnsData = (returnsData: IProFormaReturnsData, cadence: string) => {
    if (!returnsData) {
        return defaultReturnsData;
    }

    const cashFlow = cadence === Periods.MONTHLY ? returnsData.cashFlow : returnsData.yearlyCashFlow;

    const createPeriods = (key: keyof ICashFlowPeriod) => {
        const periodsData: Record<string, number | null> = {};
        cashFlow?.forEach(data => {
            if (data?.analysisDate) {
                periodsData[data.analysisDate] = data?.[key] === null ? null : Number(data?.[key]);
            }
        });
        return periodsData;
    };

    const proFormaReturnsCashFlow = {
        totalProjectCost: createPeriods('projectCost'),
        totalLoanInterest: createPeriods('constructionLoanInterest'),
        totalFinancingFees: createPeriods('financingFee'),
        totalConstructionLoanDraws: createPeriods('constructionLoanDraws'),
        totalPermanentLoanFunding: createPeriods('permanentLoanFunding'),
        totalCashFlowFromOperations: createPeriods('cashFlowFromOperations'),
        totalPermanentLoanDebtService: createPeriods('permanentLoanDebtService'),
        totalNetSaleProceeds: createPeriods('netSaleProceeds'),
        totalConstructionLoanPayoff: createPeriods('constructionLoanPayoff'),
        totalPermanentLoanPayoff: createPeriods('permanentLoanPayoff'),
        totalEquityProceedFromCapitalEvents: createPeriods('equityProceedFromCapitalEvents'),
    };

    return {
        proFormaReturns: {
            totalProjectCost: returnsData.totalProjectCost ?? null,
            totalLoanInterest: returnsData.totalLoanInterest ?? null,
            totalFinancingFees: returnsData.totalFinancingFees ?? null,
            totalConstructionLoanDraws: returnsData.totalConstructionLoanDraws ?? null,
            totalPermanentLoanFunding: returnsData.totalPermanentLoanFunding ?? null,
            totalCashFlowFromOperations: returnsData.totalCashFlowFromOperations ?? null,
            totalPermanentLoanDebtService: returnsData.totalPermanentLoanDebtService ?? null,
            totalNetSaleProceeds: returnsData.totalNetSaleProceeds ?? null,
            totalConstructionLoanPayoff: returnsData.totalConstructionLoanPayoff ?? null,
            totalPermanentLoanPayoff: returnsData.totalPermanentLoanPayoff ?? null,
            totalEquityProceedFromCapitalEvents: returnsData.totalEquityProceedFromCapitalEvents ?? null,
        },
        proFormaReturnsCashFlow,
        cashFlowData: {
            netUnleveredCashFlow: {
                equityMultiple: returnsData.netUnleveredEquityMultiple ?? null,
                irr: returnsData.netUnleveredIrr ?? null,
                netProfit: returnsData.netUnleveredNetProfit ?? null,
                amount: returnsData.totalNetUnleveredCashFlow ?? null,
                periods: createPeriods('netUnleveredCashFlow'),
            },
            netLeveredCashFlow: {
                equityMultiple: returnsData.netLeveredEquityMultiple ?? null,
                irr: returnsData.netLeveredIrr ?? null,
                amount: returnsData.totalNetLeveredCashFlow ?? null,
                netProfit: returnsData.netLeveredNetProfit ?? null,
                periods: createPeriods('netLeveredCashFlow'),
            },
        },
        costMetrics: {
            yieldOnCost: {
                untrended: returnsData.yieldOnCost?.untrended ?? null,
                trended: returnsData.yieldOnCost?.trended ?? null,
                sale: returnsData.yieldOnCost?.sale ?? null,
                isHighlighted: null,
            },
            marketCapRate: {
                untrended: returnsData.marketCapitalizationRate?.current ?? null,
                trended: returnsData.marketCapitalizationRate?.trended ?? null,
                sale: returnsData.marketCapitalizationRate?.onSale ?? null,
                isHighlighted: null,
            },
            developmentSpread: {
                untrended: returnsData.developmentSpread?.untrended ?? null,
                trended: returnsData.developmentSpread?.trended ?? null,
                sale: returnsData.developmentSpread?.sale ?? null,
                isHighlighted: null,
            },
        },
    };
};

export const getPeriodKeys = (returnsData: IProFormaReturnsData, cadence : string) => {
    const cashFlow = cadence === Periods.MONTHLY ? returnsData.cashFlow : returnsData.yearlyCashFlow;
    if (!cashFlow?.length || cashFlow.length === 1) return [];
    return cashFlow.map(cf => cf.analysisDate);
};
